import { ParamMap } from '@angular/router'
import type { AppTableFilter } from './table.component'
import {
  ANYTIME_PRESET,
  CUSTOM_DATE_PRESET,
  DateFilterValue,
  DateRange,
  isDatePreset,
  resolveCustomRange,
  resolveDateRange,
  toDateInputValue,
} from './table-date-presets'

/** What writing one column to the URL, and reading it back, needs to know about it. */
export type TableUrlColumn = {
  id: string
  filter?: AppTableFilter | null
  numeric?: boolean
  sortable?: boolean
  defaultDatePreset?: string
  defaultFacetValues?: (string | number)[]
}

export type TableUrlSort = { columnId: string; direction: 'asc' | 'desc' } | null

/** The sorting and the filters in the shape the table's own state holds them. */
export type TableUrlState = {
  sorting: { id: string; desc: boolean }[]
  filters: { id: string; value: unknown }[]
  /** The two ends of a stated window, as the date inputs hold them. Empty where none was stated. */
  statedWindow: { from: string; to: string }
}

/** One table's query keys and the values the URL carries for them. Only the keys actually set. */
export type TableUrlParams = Record<string, string[]>

/**
 * Says the reader turned the sorting off, which the absence of the key cannot: that is also what a
 * URL written before this table had any sorting looks like, and there the table's default stands.
 */
const NO_SORT = 'none'

/** An explicit empty facet, distinct from an absent key that allows the column default to apply. */
const NO_FACET_FILTER = ''

const DESCENDING = '-'

function withPrefix(prefix: string | null, key: string): string {
  return prefix === null ? key : `${prefix}.${key}`
}

function sortKey(prefix: string | null): string {
  return withPrefix(prefix, 'sort')
}

function filterKey(prefix: string | null, columnId: string, end?: 'from' | 'to'): string {
  return withPrefix(prefix, end === undefined ? `filter.${columnId}` : `filter.${columnId}.${end}`)
}

function isFilterable(column: TableUrlColumn): boolean {
  return (column.filter ?? null) !== null
}

function first(params: TableUrlParams, key: string): string | undefined {
  return (params[key] ?? [])[0]
}

/**
 * Every key this table speaks for, whether or not it is using it. The write needs the whole set so
 * that the router's merge clears the keys the table has stopped using, rather than leaving them
 * behind to contradict it.
 */
export function tableParameterNames(prefix: string | null, columns: TableUrlColumn[]): string[] {
  const filters = columns
    .filter(isFilterable)
    .flatMap((column) =>
      column.filter === 'date'
        ? [filterKey(prefix, column.id), filterKey(prefix, column.id, 'from'), filterKey(prefix, column.id, 'to')]
        : [filterKey(prefix, column.id)]
    )
  return [sortKey(prefix), ...filters]
}

export function readTableUrlParams(paramMap: ParamMap, prefix: string | null, columns: TableUrlColumn[]): TableUrlParams {
  return tableParameterNames(prefix, columns).reduce<TableUrlParams>((params, name) => {
    const values = paramMap.getAll(name)
    return values.length === 0 ? params : { ...params, [name]: values }
  }, {})
}

/** Null where the key is unused, which is how the router's merge is told to drop it. */
export function toQueryParams(params: TableUrlParams, prefix: string | null, columns: TableUrlColumn[]): Record<string, string[] | null> {
  return Object.fromEntries(tableParameterNames(prefix, columns).map((name) => [name, params[name] ?? null]))
}

function toSortParams(sorting: TableUrlState['sorting'], prefix: string | null, defaultSort: TableUrlSort): TableUrlParams {
  const [sort] = sorting
  if (sort !== undefined) {
    return { [sortKey(prefix)]: [`${sort.desc ? DESCENDING : ''}${sort.id}`] }
  }
  // An unsorted table has nothing to say unless it has a default the reader has turned off, which an
  // absent key would quietly bring back.
  return defaultSort === null ? {} : { [sortKey(prefix)]: [NO_SORT] }
}

function toDateParams(column: TableUrlColumn, value: DateFilterValue | undefined, prefix: string | null): TableUrlParams {
  const key = filterKey(prefix, column.id)
  if (value === undefined) {
    // As with the sorting: only a window the column opens on is worth writing down as cleared.
    return column.defaultDatePreset === undefined ? {} : { [key]: [ANYTIME_PRESET] }
  }
  if (value.presetId !== CUSTOM_DATE_PRESET) {
    // A relative preset is re-resolved against the clock when the link is opened, so the instants it
    // stands for today would only mislead whoever opens it tomorrow.
    return { [key]: [value.presetId] }
  }
  return {
    [key]: [CUSTOM_DATE_PRESET],
    ...(value.from === null ? {} : { [filterKey(prefix, column.id, 'from')]: [toDateInputValue(value.from)] }),
    ...(value.to === null ? {} : { [filterKey(prefix, column.id, 'to')]: [toDateInputValue(value.to)] }),
  }
}

function toColumnFilterParams(column: TableUrlColumn, value: unknown, prefix: string | null): TableUrlParams {
  if (column.filter === 'date') {
    return toDateParams(column, value as DateFilterValue | undefined, prefix)
  }
  if (column.filter === 'text') {
    const text = typeof value === 'string' ? value : ''
    return text === '' ? {} : { [filterKey(prefix, column.id)]: [text] }
  }
  const selected = (value as (string | number)[] | undefined) ?? []
  if (selected.length === 0) {
    return column.defaultFacetValues === undefined ? {} : { [filterKey(prefix, column.id)]: [NO_FACET_FILTER] }
  }
  return { [filterKey(prefix, column.id)]: selected.map(String) }
}

/** The table's sorting and filters as the URL should carry them. */
export function toTableUrlParams(
  state: Pick<TableUrlState, 'sorting' | 'filters'>,
  prefix: string | null,
  columns: TableUrlColumn[],
  defaultSort: TableUrlSort
): TableUrlParams {
  const values = new Map(state.filters.map((filter) => [filter.id, filter.value]))
  return columns
    .filter(isFilterable)
    .reduce<TableUrlParams>(
      (params, column) => ({ ...params, ...toColumnFilterParams(column, values.get(column.id), prefix) }),
      toSortParams(state.sorting, prefix, defaultSort)
    )
}

function canSort(columns: TableUrlColumn[], columnId: string): boolean {
  return columns.some((column) => column.id === columnId && column.sortable === true)
}

function toSorting(params: TableUrlParams, prefix: string | null, columns: TableUrlColumn[], defaultSort: TableUrlSort): TableUrlState['sorting'] {
  const value = first(params, sortKey(prefix))
  if (value === NO_SORT) {
    return []
  }
  if (value !== undefined) {
    const desc = value.startsWith(DESCENDING)
    const columnId = desc ? value.slice(DESCENDING.length) : value
    // A link naming a column this table cannot sort by falls through to the default rather than
    // opening unsorted, which would read as a choice the reader had made.
    if (canSort(columns, columnId)) {
      return [{ id: columnId, desc }]
    }
  }
  return defaultSort === null || !canSort(columns, defaultSort.columnId) ? [] : [{ id: defaultSort.columnId, desc: defaultSort.direction === 'desc' }]
}

function readStatedRange(params: TableUrlParams, prefix: string | null, columnId: string): DateRange {
  return resolveCustomRange(first(params, filterKey(prefix, columnId, 'from')) ?? '', first(params, filterKey(prefix, columnId, 'to')) ?? '')
}

function toDateFilterValue(column: TableUrlColumn, params: TableUrlParams, prefix: string | null, now: number): DateFilterValue | undefined {
  const named = first(params, filterKey(prefix, column.id))
  // A value naming no preset this table knows is treated as unsaid, so a stale or mistyped link opens
  // on the window the column opens on rather than on nothing.
  const presetId = (named !== undefined && isDatePreset(named) ? named : undefined) ?? column.defaultDatePreset
  if (presetId === undefined || presetId === ANYTIME_PRESET) {
    return undefined
  }
  const range = presetId === CUSTOM_DATE_PRESET ? readStatedRange(params, prefix, column.id) : resolveDateRange(presetId, now)
  return range.from === null && range.to === null ? undefined : { presetId, ...range }
}

function toFilterValue(column: TableUrlColumn, params: TableUrlParams, prefix: string | null, now: number): unknown {
  if (column.filter === 'date') {
    return toDateFilterValue(column, params, prefix, now)
  }
  const values = params[filterKey(prefix, column.id)]
  if (values === undefined) {
    return column.filter === 'facet' && column.defaultFacetValues !== undefined ? [...column.defaultFacetValues] : undefined
  }
  if (values.length === 0 || (column.filter === 'facet' && values.length === 1 && values[0] === NO_FACET_FILTER)) {
    return undefined
  }
  if (column.filter === 'text') {
    return values[0] === '' ? undefined : values[0]
  }
  // A facet on a numeric column holds numbers and the client-side predicate compares by identity, so
  // the strings the URL hands back have to become numbers again.
  return column.numeric === true ? values.map(Number) : values
}

/** The window a date column was given by hand, so reopening its popover shows the dates it holds. */
function toStatedWindow(params: TableUrlParams, prefix: string | null, columns: TableUrlColumn[]): TableUrlState['statedWindow'] {
  const stated = columns.find((column) => column.filter === 'date' && first(params, filterKey(prefix, column.id)) === CUSTOM_DATE_PRESET)
  if (stated === undefined) {
    return { from: '', to: '' }
  }
  return { from: first(params, filterKey(prefix, stated.id, 'from')) ?? '', to: first(params, filterKey(prefix, stated.id, 'to')) ?? '' }
}

/**
 * Where the URL says the table should be, with the table's own defaults filling in what it leaves
 * unsaid. Complete rather than partial: what comes back is the whole of the sorting and the filters,
 * so a key the URL omits means unfiltered and not "leave whatever is there".
 */
export function toTableUrlState(
  params: TableUrlParams,
  prefix: string | null,
  columns: TableUrlColumn[],
  defaultSort: TableUrlSort,
  now: number
): TableUrlState {
  return {
    sorting: toSorting(params, prefix, columns, defaultSort),
    filters: columns.filter(isFilterable).reduce<TableUrlState['filters']>((filters, column) => {
      const value = toFilterValue(column, params, prefix, now)
      return value === undefined ? filters : [...filters, { id: column.id, value }]
    }, []),
    statedWindow: toStatedWindow(params, prefix, columns),
  }
}
